import { token } from '@atlaskit/tokens';
import React, {
  type CSSProperties,
  type FunctionComponent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { messages } from './i18n';

export interface BaseResizeHandleProps {
  id: string;
  disabled?: boolean;
  style?: CSSProperties;
  onDragging?: (isDragging: boolean) => void;
  minSize: number;
  maxSize: number;
  currentSize: number | null;
  onPointerResize: (delta: number, startSize: number) => void;
  onKeyboardResize: (delta: number, startSize: number) => void;
  keyboardStep?: number;
}

const HIT_AREA_MARGIN = token('space.negative.050', '-4px');

const ResizeHandle = styled.div`
  flex: 0 0 0;
  position: relative;
  outline: none;
  touch-action: none;
  user-select: none;
  cursor: col-resize;

  &::before {
    content: '';
    position: absolute;
    top: ${token('space.0', '0')};
    bottom: ${token('space.0', '0')};
    left: ${HIT_AREA_MARGIN};
    right: ${HIT_AREA_MARGIN};
  }

  &[data-disabled='true'] {
    cursor: default;
    pointer-events: none;
  }
`;

const BaseResizeHandle: FunctionComponent<BaseResizeHandleProps> = ({
  id,
  disabled = false,
  style,
  onDragging,
  minSize,
  maxSize,
  currentSize,
  onPointerResize,
  onKeyboardResize,
  keyboardStep = 1,
}) => {
  const { formatMessage } = useIntl();
  const handleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dragStartRef = useRef<{ startX: number; startSize: number } | null>(null);

  const KEYBOARD_STEP = keyboardStep;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled || currentSize === null) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          onKeyboardResize(KEYBOARD_STEP, currentSize);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          onKeyboardResize(-KEYBOARD_STEP, currentSize);
          break;
        case 'Home':
          event.preventDefault();
          onKeyboardResize(maxSize - currentSize, currentSize);
          break;
        case 'End':
          event.preventDefault();
          onKeyboardResize(minSize - currentSize, currentSize);
          break;
        default:
          break;
      }
    },
    [disabled, currentSize, minSize, maxSize, onKeyboardResize, KEYBOARD_STEP]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (disabled || currentSize === null) {
        return;
      }

      event.preventDefault();
      const target = event.currentTarget as HTMLElement;
      target.setPointerCapture(event.pointerId);

      dragStartRef.current = {
        startX: event.clientX,
        startSize: currentSize,
      };

      setIsDragging(true);
      onDragging?.(true);
    },
    [disabled, currentSize, onDragging]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging || !dragStartRef.current || disabled) {
        return;
      }

      const delta = dragStartRef.current.startX - event.clientX;
      onPointerResize(delta, dragStartRef.current.startSize);
    },
    [isDragging, disabled, onPointerResize]
  );

  const handlePointerUp = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging) {
        return;
      }

      const target = event.currentTarget as HTMLElement;
      target.releasePointerCapture(event.pointerId);

      dragStartRef.current = null;
      setIsDragging(false);
      onDragging?.(false);
    },
    [isDragging, onDragging]
  );

  const handleMouseEnter = useCallback(() => {
    if (!disabled) {
      setIsHovered(true);
    }
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleFocus = useCallback(() => {
    if (!disabled) {
      setIsFocused(true);
    }
  }, [disabled]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const getResizeHandleState = () => {
    if (isDragging) {
      return 'drag';
    }
    if (isHovered || isFocused) {
      return 'hover';
    }
    return 'inactive';
  };

  const ariaValueNow = currentSize !== null ? Math.round(currentSize) : undefined;

  return (
    <ResizeHandle
      ref={handleRef}
      id={id}
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={formatMessage(messages['layout.resize-handle.aria-label'])}
      aria-valuemin={Math.round(minSize)}
      aria-valuemax={Math.round(maxSize)}
      aria-valuenow={ariaValueNow}
      aria-orientation="horizontal"
      aria-disabled={disabled}
      data-dragging={isDragging}
      data-disabled={disabled}
      data-resize-handle-state={getResizeHandleState()}
      style={style}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};

export default BaseResizeHandle;
